/**
 * Web Component entry point for the Points History widget.
 *
 * Importing this module registers the <legion-points-history> custom
 * element so it can be used in any HTML page or framework that supports
 * web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/points-history-web-component';
 *
 * // Then use in HTML:
 * // <legion-points-history api-url="https://your-domain.com" auth-token="jwt" />
 * ```
 */

import { POINTS_HISTORY_THEMING_PROP_TO_PARAM } from "./types";
import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

const POINTS_HISTORY_CSS_ATTRIBUTES = Object.values(
  POINTS_HISTORY_THEMING_PROP_TO_PARAM
);

class LegionPointsHistoryElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      "width",
      "height",
      "theme",
      "show-close",
      ...POINTS_HISTORY_CSS_ATTRIBUTES,
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "600";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get showClose(): boolean {
    const v = this.getAttribute("show-close");
    return v === "true" || v === "";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/points-history", this.apiUrl);
    url.searchParams.set("theme", this.theme);
    if (this.showClose) url.searchParams.set("showClose", "true");

    POINTS_HISTORY_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = "100%";
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Points History Widget";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "points-history", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load points history widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth};
      }
      iframe {
        width: 100%;
        border: none;
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-points-history")
) {
  customElements.define("legion-points-history", LegionPointsHistoryElement);
}

export { LegionPointsHistoryElement };
